/**
 * @fileoverview SalesRequestError: a failed Sales CRM request's message, with Sign in again for a
 * recent-sign-in refusal.
 * @description Assigning prospect owners (and listing who may be assigned) needs a fresh MFA
 * sign-in (`403 / 2009`, within `IDENTITY_STEP_UP_MFA_MAX_AGE_MINUTES`). The backend has no
 * separate step-up endpoint yet, so — as in Invite User — the way to refresh it is to sign in again.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { requestErrorMessage } from '@/features/sales/lib/request-error.utils'
import { ApiException } from '@/shared/api/api.exception'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'
import { useLogout } from '@/shared/lib/auth/useLogout'

interface SalesRequestErrorProps {
  error: Error
}

/**
 * @component
 * @param {SalesRequestErrorProps} props - The error to show.
 * @param {Error} props.error - The failed request's error.
 */
function SalesRequestError({ error }: SalesRequestErrorProps) {
  const { t } = useTranslation()
  const logoutMutation = useLogout()
  const needsFreshSignIn =
    error instanceof ApiException && error.code === ERROR_CODES.STEP_UP_MFA_REQUIRED

  return (
    <div role="alert" className="text-[13px] text-danger" data-testid="sales-request-error">
      <p>{requestErrorMessage(error, t)}</p>
      {needsFreshSignIn && (
        <>
          <p className="mt-1 text-slate">{t('sales.pipeline.stepUp.hint')}</p>
          <button
            type="button"
            onClick={() => logoutMutation.mutate()}
            data-testid="sales-request-error-sign-in-button"
            className="mt-1 cursor-pointer font-semibold text-primary underline"
          >
            {t('sales.pipeline.stepUp.signInAgain')}
          </button>
        </>
      )}
    </div>
  )
}

export default SalesRequestError
